import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { STATUS_LABEL } from "@/lib/statusLabels";
import { Wallet, TrendingUp, FileWarning, CheckCircle2, Clock, XCircle } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, o] = await Promise.all([api.get("/admin/stats"), api.get("/admin/orders?limit=6&page=1")]);
      setStats(s.data);
      setRecent(o.data.orders);
    })();
  }, []);

  const cards = stats ? [
    { label: "Total Orders", value: stats.total_orders, icon: Wallet, tint: "bg-blue-50 text-[#1E3A8A]" },
    { label: "Pending", value: stats.pending, icon: Clock, tint: "bg-amber-50 text-amber-700" },
    { label: "In Review", value: stats.in_review, icon: FileWarning, tint: "bg-indigo-50 text-indigo-700" },
    { label: "Processing", value: stats.processing, icon: TrendingUp, tint: "bg-sky-50 text-sky-700" },
    { label: "Completed", value: stats.completed, icon: CheckCircle2, tint: "bg-emerald-50 text-emerald-700" },
    { label: "Failed Payments", value: stats.failed_payments, icon: XCircle, tint: "bg-red-50 text-red-700" },
  ] : [];

  return (
    <DashboardLayout admin>
      <div className="mb-8">
        <div className="section-label">Admin</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Overview</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="card-crisp rounded-xl p-5" data-testid={`admin-stat-${c.label.replace(/\s/g, "-").toLowerCase()}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.tint}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-4 text-3xl font-display font-bold text-[#0A192F]">{c.value}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-0.5">{c.label}</div>
            </div>
          );
        })}
        <div className="rounded-xl p-5 lg:col-span-3 bg-gradient-to-br from-[#0A192F] to-[#1E3A8A] text-white shadow-lg border border-[#1E3A8A]/40" data-testid="admin-revenue-card">
          <div className="section-label text-white/70">Revenue (Paid Orders)</div>
          <div className="mt-2 font-display font-bold text-4xl text-white">₹{(stats?.revenue || 0).toLocaleString("en-IN")}</div>
          <div className="mt-1 text-sm text-white/70">Total collected across all services</div>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-white">
        <div className="p-5 border-b border-slate-200">
          <div className="section-label">Recent Orders</div>
          <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Latest 6</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Order #</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono">{o.order_number || "—"}</td>
                  <td className="px-4 py-3">{o.full_name}<div className="text-xs text-slate-500">{o.email}</div></td>
                  <td className="px-4 py-3">{o.service_name}</td>
                  <td className="px-4 py-3"><span className="status-badge badge-info">{STATUS_LABEL[o.current_status]}</span></td>
                  <td className="px-4 py-3 text-right">₹{o.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
